import React, { useContext, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { SmsOptInFinePrint } from 'registration/components/SmsOptInFinePrint';
import { SmsOptInNudgeModal } from 'registration/components/SmsOptInNudgeModal';
import {
  Onboarding,
  OnboardingContext,
  OnboardingText,
  OnboardingTitle,
} from 'registration/containers/Onboarding';
import { getPhoneText } from 'registration/helpers/phone';
import { isSmsOptInVariant } from 'registration/helpers/smsOptInVariant';
import { OnboardingData } from 'registration/types';

import { Center } from 'shared/components/ihcl/positioning';
import { styled } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';
import { Checkbox } from 'shared/components/ihcl/v2/checkbox';
import { Input } from 'shared/components/ihcl/v2/input';
import { doDelete, doPost } from 'shared/helpers/http';
import { parsePhoneNumber } from 'shared/helpers/parsers';
import { trackClick, trackEvent } from 'shared/helpers/tracking';

import OnboardingTestimonial from './OnboardingTestimonial';

const SignInWrapper = styled('div', {
  marginTop: '32px',
});

const TextLoginWrapper = styled('div', {
  marginTop: '8px',
});

const OnboardingPhone = ({
  nextAction = null,
  previousAction = null,
  stepNumber,
}) => {
  const { onboardingBasePath, onboardingData, setOnboardingData } =
    useContext(OnboardingContext);
  const nextActionButtonRef = useRef(null);
  const [smsSent, setSMSSent] = useState(false);
  const [smsSending, setSMSSending] = useState(false);
  const [isNudgeOpen, setIsNudgeOpen] = useState(false);
  const pendingSmsOptInRef = useRef<boolean | null>(null);
  const skipNudgeRef = useRef(false);
  const smsOptIn = onboardingData?.sms_opt_in === true;
  const showSmsOptIn = isSmsOptInVariant(onboardingBasePath);

  useEffect(() => {
    if (
      pendingSmsOptInRef.current === null ||
      onboardingData?.sms_opt_in !== pendingSmsOptInRef.current
    ) {
      return;
    }
    pendingSmsOptInRef.current = null;
    skipNudgeRef.current = true;
    nextActionButtonRef.current?.click();
    skipNudgeRef.current = false;
  }, [onboardingData]);

  if (!onboardingData || Object.keys(onboardingData).length < 1) {
    return null;
  }
  const { errors, phone } = onboardingData;
  const phoneText = getPhoneText(phone) || '';

  const setSmsOptIn = (checked: boolean) => {
    setOnboardingData({
      ...onboardingData,
      sms_opt_in: checked,
    });
  };

  const validateBeforeSubmit = () => {
    if (!showSmsOptIn || smsOptIn || skipNudgeRef.current) {
      return true;
    }
    trackEvent('Onboarding Phone SMS Opt In Nudge Shown');
    setIsNudgeOpen(true);
    return false;
  };

  const continueWithSmsOptIn = (checked: boolean) => {
    trackClick(
      checked
        ? 'Onboarding Phone SMS Opt In Nudge Turn On'
        : 'Onboarding Phone SMS Opt In Nudge Continue Without'
    );
    setIsNudgeOpen(false);
    pendingSmsOptInRef.current = checked;
    setSmsOptIn(checked);
  };

  let phoneError = null;
  if (errors && errors.phone && Array.isArray(errors.phone)) {
    phoneError = errors.phone[0];
  }

  const sendSMSLogin = (evt) => {
    evt.preventDefault();
    trackClick('Onboarding Duplicate Phone Send SMS Login');
    setSMSSending(true);
    doPost('/nurse_profile/send_sms_login', {}).then(() => {
      setSMSSent(true);
      setSMSSending(false);
    });
  };

  let smsLinkText = 'Text me a login link';
  if (smsSent) {
    smsLinkText = 'SMS Login Link sent. Retry?';
  }

  const setPhone = (phoneNumber, errorMessage) => {
    const phoneData = {
      phone: {
        text: phoneNumber,
      },
      disallow_duplicate_phone: true,
    };
    if (errorMessage) {
      const newErrors = {
        ...errors,
        phone: errorMessage,
      };
      setOnboardingData({
        ...onboardingData,
        ...phoneData,
        errors: newErrors,
      });
    } else if (errors) {
      const newErrors = Object.fromEntries(
        Object.entries(errors).filter(([key]) => key !== 'phone')
      );
      let newOnboardingData: OnboardingData = {
        ...onboardingData,
        ...phoneData,
        errors: newErrors,
      };
      if (Object.keys(newErrors).length === 0) {
        newOnboardingData = Object.fromEntries(
          Object.entries(newOnboardingData).filter(([key]) => key !== 'errors')
        );
      }
      setOnboardingData(newOnboardingData);
    } else {
      setOnboardingData({
        ...onboardingData,
        ...phoneData,
      });
    }
  };

  const { referral_code: referralCode } = onboardingData;

  return (
    <Onboarding
      currentStep={stepNumber}
      isNextDisabled={
        !parsePhoneNumber(phoneText) || parsePhoneNumber(phoneText).length < 12
      }
      nextAction={nextAction}
      nextActionButtonRef={nextActionButtonRef}
      previousAction={previousAction}
      stepKeys={
        showSmsOptIn
          ? ['phone', 'disallow_duplicate_phone', 'sms_opt_in']
          : ['phone', 'disallow_duplicate_phone']
      }
      stepName="Phone"
      validateBeforeSubmit={validateBeforeSubmit}
    >
      <form
        action="#"
        onSubmit={(evt) => {
          evt.preventDefault();
          if (
            parsePhoneNumber(phoneText) &&
            nextActionButtonRef &&
            nextActionButtonRef.current
          ) {
            nextActionButtonRef.current.click();
          }
        }}
      >
        <Center>
          <OnboardingTitle>What&apos;s your phone number?</OnboardingTitle>
          <Center>
            <OnboardingText>
              It&apos;s used for interview scheduling, and we take your privacy
              seriously.
            </OnboardingText>
            <Input
              value={phoneText}
              inputMode="tel"
              type="tel"
              label="Phone number"
              onChange={(evt) => {
                setPhone(evt.target.value, null);
              }}
              onBlur={(evt) => {
                if (!parsePhoneNumber(evt.target.value)) {
                  setPhone(
                    evt.target.value,
                    'Please enter a valid phone number'
                  );
                }
              }}
              status={phoneError ? 'error' : 'default'}
              caption={phoneError}
              className="text-left"
            />
            {phoneError === 'Looks like you already have an account' && (
              <>
                <SignInWrapper>
                  <Button
                    variant="minimal"
                    onClick={(evt) => {
                      evt.preventDefault();
                      trackClick('Onboarding Duplicate Phone Sign In');
                      doDelete('/users/sign_out', {}).then(() => {
                        window.location.href = `/users/sign_in?migrate_from=${referralCode}`;
                      });
                    }}
                  >
                    Sign In
                  </Button>
                </SignInWrapper>
                <TextLoginWrapper>
                  <Button
                    variant="minimal"
                    loading={smsSending}
                    onClick={sendSMSLogin}
                  >
                    {smsLinkText}
                  </Button>
                </TextLoginWrapper>
              </>
            )}

            {showSmsOptIn ? (
              <>
                <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-body-copy-gray/12 bg-white p-4 text-left">
                  <Checkbox
                    checked={smsOptIn}
                    onCheckedChange={(checked) => {
                      trackClick('Onboarding Phone SMS Opt In Toggled', {
                        checked,
                      });
                      setSmsOptIn(checked === true);
                    }}
                    color="alternate"
                    size="sm"
                  />
                  <span className="flex flex-col gap-1">
                    <span className="text-label-md font-extrabold text-gray-body-copy">
                      Yes, text me job matches
                    </span>
                    <span className="text-paragraph-sm font-normal text-gray-body-copy">
                      Hear about new matches and interview requests first.
                    </span>
                  </span>
                </label>

                <SmsOptInFinePrint className="mt-4" />

                <SmsOptInNudgeModal
                  open={isNudgeOpen}
                  onOpenChange={setIsNudgeOpen}
                  onTurnOnTextAlerts={() => continueWithSmsOptIn(true)}
                  onContinueWithoutTextAlerts={() =>
                    continueWithSmsOptIn(false)
                  }
                />
              </>
            ) : (
              <SmsOptInFinePrint variant="legacy" className="mt-4" />
            )}

            <OnboardingTestimonial
              attributionLine="#humbleBrag from:"
              author="Carley Hansen, RN"
              quote="I would definitely recommend this service! They are prompt, responsive, and a great resource. I was able to secure interviews and a job in less than 2 weeks."
            />
          </Center>
        </Center>
      </form>
    </Onboarding>
  );
};

OnboardingPhone.propTypes = {
  nextAction: PropTypes.string,
  previousAction: PropTypes.string,
  stepNumber: PropTypes.number.isRequired,
};

export default OnboardingPhone;
